import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, X } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { updateCategoryPreKey } from "@/apis/category/route";
import {
  DefinitionRoleInfo,
  PresentationRoleInfo,
} from "@/apis/category/types";
import { assignCellRoles } from "@/apis/table/route";

import { useCategoryId, useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { useMainStore } from "@/stores/main";
import { useMappingStore } from "@/stores/mapping";

import { cn } from "@/lib/utils";
import { Button } from "@/styles/ui/button";
import { Checkbox } from "@/styles/ui/checkbox";
import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/styles/ui/command";

import { ApiError } from "@/apis/errors";
import { ReportType, TypeChip } from "@/components/common/report-chip";
import { getMergeAwareCellsFromRange } from "@/utils/cell";
import { filterBySearchText } from "@/utils/search-text";
import { useTableId } from "../hooks";

/**
 * preKey 의 statement_digit (마지막 글자) 으로 보고서 종류 추출.
 *   0 → CONSOLIDATED (연결)
 *   5 → SEPARATE (별도)
 *   그 외 → null (TypeChip 안 그림)
 */
const reportTypeOfPreKey = (preKey: string): ReportType | null => {
  const last = preKey.slice(-1);
  if (last === "0") return "CONSOLIDATED";
  if (last === "5") return "SEPARATE";
  return null;
};

type OpenCard = "presentation" | "definition" | null;

/**
 * 주석목차 선택 패널.
 *
 * PL/DL 두 인라인 콤보 + "주석목차 전체에 적용" 체크박스 구조.
 * - 한 번에 하나만 펼침. 펼친 카드가 패널의 남은 공간을 flex-1 로 차지
 * - PL 디폴트: cell roleKey prefix 빈도 최고 (없으면 category preRole 폴백)
 * - PL 선택 시 DL 자동 초기화 + DL 자동 펼침
 * - 백엔드가 type 분기(HEADER/NONE → DATA promote, SUM 유지)를 흡수 → 단일 endpoint
 */
export const RoleSelectionPanel = ({
  resetViewState,
}: {
  resetViewState: () => void;
}) => {
  const { data: workbookId } = useWorkbookId();
  const { data: categoryId } = useCategoryId();
  const {
    data: { tableId },
  } = useTableId();

  const selectedFact = useMappingStore((s) => s.fact.selected);
  const currentSelection = useMainStore((s) => s.selection.current);
  const dragAddressRange =
    currentSelection?.address ?? selectedFact?.address ?? null;

  // 머지 셀 보정용 — 백엔드 cell address(머지 셀은 "A1:B2" 형태)를 SSOT 로 사용.
  const { data: cellsData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
    enabled: !!workbookId && !!tableId,
  });

  const [selectedPre, setSelectedPre] = useState<PresentationRoleInfo | null>(
    null,
  );
  const [selectedDef, setSelectedDef] = useState<DefinitionRoleInfo | null>(
    null,
  );
  const [applyToCategory, setApplyToCategory] = useState(false);
  const [openCard, setOpenCard] = useState<OpenCard>(null);

  useDefaultPresentation({
    workbookId,
    tableId,
    categoryId,
    selectedPre,
    onResolved: setSelectedPre,
  });

  const handlePreSelect = (role: PresentationRoleInfo) => {
    setSelectedPre(role);
    setSelectedDef(null);
    setOpenCard("definition");
  };

  const handleDefSelect = (role: DefinitionRoleInfo) => {
    setSelectedDef(role);
    setOpenCard(null);
  };

  const queryClient = useQueryClient();

  /**
   * role 변경 후 워크북 하위 모든 쿼리(cells, taxonomy, role 등)를 갱신한다.
   * useCellStyles 의 useLayoutEffect 가 cells 쿼리에 의존하므로,
   * refetch 완료까지 기다린 후 panel 을 닫아야 cell 배경색 등 재렌더링이
   * panel 닫히는 시점에 이미 적용된 상태가 된다. await 빠뜨리면 stale 상태가
   * 잠깐 보이는 깜빡임이 생긴다.
   */
  const invalidateWorkbook = async () => {
    if (!workbookId) return;
    await queryClient.invalidateQueries({
      queryKey: queries.workbook(workbookId).queryKey,
    });
  };

  const { mutateAsync: applyRoles, isPending: isApplying } = useMutation({
    mutationFn: async () => {
      if (!workbookId || !tableId || !categoryId || !dragAddressRange) {
        throw new Error("선택 정보가 부족합니다.");
      }
      if (!selectedPre || !selectedDef) {
        throw new Error("PL/DL을 모두 선택해주세요.");
      }
      if (!cellsData) {
        throw new Error("셀 정보를 불러오는 중입니다.");
      }

      const mergedRanges = cellsData.cells
        .filter((cell) => cell.address.includes(":"))
        .map((cell) => cell.address);
      const cellAddresses = getMergeAwareCellsFromRange(
        dragAddressRange,
        mergedRanges,
      );
      if (cellAddresses.length === 0) {
        throw new Error("적용할 셀이 없습니다.");
      }

      if (applyToCategory) {
        await updateCategoryPreKey(workbookId, categoryId, {
          preKey: selectedPre.preKey,
        });
      }

      await assignCellRoles(workbookId, tableId, {
        cellAddresses,
        definitionRoleKey: selectedDef.key,
      });
    },
    onSuccess: async () => {
      toast.success("주석 목차가 수정되었습니다.");
      await invalidateWorkbook();
      resetViewState();
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.errorCode === "INVALID_INPUT") {
        toast.error("표 안의 셀들을 정확하게 선택했는지 확인해주세요.");
        return null;
      } else {
        toast.error("주석 목차 변경에 실패했습니다.");
      }
    },
  });

  const canApply = Boolean(selectedPre && selectedDef && !isApplying);

  return (
    <div className="bg-white h-full w-[500px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] flex flex-col">
      <PanelHeader onClose={resetViewState} />

      <div className="flex-1 min-h-0 p-4 flex flex-col gap-3">
        <PresentationCombobox
          workbookId={workbookId}
          categoryId={categoryId}
          value={selectedPre}
          onChange={handlePreSelect}
          open={openCard === "presentation"}
          onOpenChange={(o) => setOpenCard(o ? "presentation" : null)}
        />
        <DefinitionCombobox
          workbookId={workbookId}
          tableId={tableId}
          preKey={selectedPre?.preKey ?? null}
          value={selectedDef}
          onChange={handleDefSelect}
          onDefaultResolved={setSelectedDef}
          open={openCard === "definition"}
          onOpenChange={(o) => setOpenCard(o ? "definition" : null)}
        />

        <label className="mt-auto flex items-center gap-2 px-1 pt-2 cursor-pointer select-none">
          <Checkbox
            checked={applyToCategory}
            onCheckedChange={(v) => setApplyToCategory(v === true)}
          />
          <span className="text-[13px] text-neutral-600">
            대목차 전체에 주석목차 적용 (표 유형은 변경되지 않음)
          </span>
        </label>
      </div>

      <div className="border-t border-neutral-100 p-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={resetViewState}>
          취소
        </Button>
        <Button disabled={!canApply} onClick={() => applyRoles()}>
          변경
        </Button>
      </div>
    </div>
  );
};

const PanelHeader = ({ onClose }: { onClose: () => void }) => (
  <div className="h-10 px-2 py-[11px] border-b border-neutral-100 flex justify-between items-center">
    <p className="text-[14px] font-medium text-neutral-600 leading-[1.429] tracking-[0.203px]">
      주석 목차 선택
    </p>
    <Button
      variant="ghost"
      onClick={onClose}
      className="p-1 w-6 h-6 hover:bg-black/[.03] active:bg-black/6"
    >
      <X className="w-4 h-4" />
    </Button>
  </div>
);

/**
 * 진입 시 PL 디폴트 설정.
 * 현재 table 의 cell roleKey prefix(= preKey) 중 빈도 최고를 우선 선택.
 * 태깅된 cell 이 없으면 현재 category 의 preRole 로 폴백한다.
 * 이미 사용자가 PL 을 골랐으면 덮어쓰지 않는다.
 */
const useDefaultPresentation = ({
  workbookId,
  tableId,
  categoryId,
  selectedPre,
  onResolved,
}: {
  workbookId: string | null | undefined;
  tableId: string | null | undefined;
  categoryId: string | null;
  selectedPre: PresentationRoleInfo | null;
  onResolved: (role: PresentationRoleInfo) => void;
}) => {
  const enabled = Boolean(workbookId && !selectedPre);

  const { data: rolesData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.presentationRoles(),
    enabled: enabled && Boolean(tableId),
  });
  const { data: cellsData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
    enabled: enabled && Boolean(tableId),
  });
  const { data: categoriesData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.categories(),
    enabled: enabled && Boolean(categoryId),
  });

  useEffect(() => {
    if (selectedPre) return;

    // 폴백이 cell 기반 결과를 앞질러 덮어쓰지 않도록, cell 데이터 로드 완료 전엔 판정 보류.
    const cellReady = !tableId || (Boolean(rolesData) && Boolean(cellsData));
    if (!cellReady) return;

    if (rolesData && cellsData) {
      const count = new Map<string, number>();
      for (const c of cellsData.cells) {
        const rk = c.taggedInfos.roleKey;
        if (!rk) continue;
        const sep = rk.lastIndexOf("_");
        if (sep <= 0) continue;
        const preKey = rk.slice(0, sep);
        count.set(preKey, (count.get(preKey) ?? 0) + 1);
      }
      let bestKey: string | null = null;
      let bestCount = 0;
      for (const [k, cnt] of count) {
        if (cnt > bestCount) {
          bestCount = cnt;
          bestKey = k;
        }
      }
      if (bestKey) {
        const role = rolesData.roles.find((r) => r.preKey === bestKey);
        if (role) {
          onResolved(role);
          return;
        }
      }
    }

    if (categoriesData && categoryId) {
      const category = categoriesData.categories.find(
        (c) => c.id === categoryId,
      );
      if (!category) return;
      onResolved({
        id: category.preRole.key,
        preKey: category.preRole.key,
        name: category.preRole.label,
        description: "",
      });
    }
  }, [
    selectedPre,
    tableId,
    rolesData,
    cellsData,
    categoriesData,
    categoryId,
    onResolved,
  ]);
};

const PresentationCombobox = ({
  workbookId,
  categoryId,
  value,
  onChange,
  open,
  onOpenChange,
}: {
  workbookId: string | null | undefined;
  categoryId: string | null;
  value: PresentationRoleInfo | null;
  onChange: (role: PresentationRoleInfo) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const enabled = Boolean(workbookId && open);
  const { data, isLoading, isError } = useQuery({
    ...queries.workbook(workbookId!)._ctx.presentationRoles(),
    enabled,
  });

  // 현재 category 의 preRole 과, 거기서 추론한 workbook statement_digit (연결=0 / 별도=5).
  // 한 workbook 안의 categories 는 모두 같은 statement_digit 이라는 가정.
  const { data: categoriesData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.categories(),
    enabled: Boolean(workbookId),
  });

  const currentPreKey = useMemo<string | null>(() => {
    if (!categoriesData || !categoryId) return null;
    const cat = categoriesData.categories.find((c) => c.id === categoryId);
    return cat?.preRole.key ?? null;
  }, [categoriesData, categoryId]);

  const preferredDigit = useMemo<"0" | "5" | null>(() => {
    if (!currentPreKey) return null;
    const last = currentPreKey.slice(-1);
    return last === "0" || last === "5" ? last : null;
  }, [currentPreKey]);

  const currentReportType: ReportType | null =
    preferredDigit === "0"
      ? "CONSOLIDATED"
      : preferredDigit === "5"
        ? "SEPARATE"
        : null;

  // 두 그룹으로 분리.
  //   primary: 현재 category preRole 고정 최상단 + 같은 statement_digit 그룹
  //   other:   반대 statement_digit 그룹 (더보기 토글 대상)
  // preferredDigit 추출 못 한 경우(드뭄)엔 전체를 primary 에 둔다.
  const { primaryGroup, otherGroup } = useMemo(() => {
    const raw = data?.roles ?? [];
    const sorted = [...raw].sort((a, b) => {
      if (currentPreKey) {
        if (a.preKey === currentPreKey) return -1;
        if (b.preKey === currentPreKey) return 1;
      }
      return a.preKey.localeCompare(b.preKey);
    });
    if (!preferredDigit) {
      return {
        primaryGroup: sorted,
        otherGroup: [] as PresentationRoleInfo[],
      };
    }
    const primary: PresentationRoleInfo[] = [];
    const other: PresentationRoleInfo[] = [];
    for (const r of sorted) {
      if (r.preKey.endsWith(preferredDigit)) primary.push(r);
      else other.push(r);
    }
    return { primaryGroup: primary, otherGroup: other };
  }, [data, currentPreKey, preferredDigit]);

  // 더보기 토글. 콤보 닫힐 때 초기화해서 다음에 열 땐 primary 만 보이도록.
  const [showOther, setShowOther] = useState(false);
  useEffect(() => {
    if (!open) setShowOther(false);
  }, [open]);

  const items = useMemo(
    () => (showOther ? [...primaryGroup, ...otherGroup] : primaryGroup),
    [primaryGroup, otherGroup, showOther],
  );

  const otherTypeLabel = preferredDigit === "0" ? "별도" : "연결";

  return (
    <RoleCombobox
      label="주석 목차"
      placeholder="주석 목차를 선택하세요"
      searchPlaceholder="key 또는 이름으로 검색"
      open={open}
      onOpenChange={onOpenChange}
      isLoading={isLoading}
      isError={isError}
      items={items}
      getItemKey={(r) => r.preKey}
      getSearchHaystack={(r) => `${r.preKey} ${r.name}`}
      isSelected={(r) => r.preKey === value?.preKey}
      onSelect={onChange}
      renderTrigger={() => {
        if (!value) return null;
        const rType = reportTypeOfPreKey(value.preKey);
        // 현재 워크북과 같은 종류면 chip 숨김 — 노이즈 감소.
        const shouldShowType = rType !== null && rType !== currentReportType;
        return (
          <RoleTriggerContent
            badge={value.preKey}
            title={value.name}
            reportType={shouldShowType ? rType : null}
          />
        );
      }}
      renderItem={(r) => {
        const rType = reportTypeOfPreKey(r.preKey);
        const shouldShowType = rType !== null && rType !== currentReportType;
        return (
          <RoleItem
            badge={r.preKey}
            title={r.name}
            description={r.description}
            reportType={shouldShowType ? rType : null}
          />
        );
      }}
      listFooter={
        !showOther && otherGroup.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowOther(true)}
            className="w-full px-3 py-2 text-[13px] text-neutral-500 hover:bg-black/[.03] flex items-center justify-center gap-1 border-t border-neutral-100"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            {otherTypeLabel} 더보기 ({otherGroup.length}개)
          </button>
        ) : null
      }
    />
  );
};

const DefinitionCombobox = ({
  workbookId,
  tableId,
  preKey,
  value,
  onChange,
  onDefaultResolved,
  open,
  onOpenChange,
}: {
  workbookId: string | null | undefined;
  tableId: string | null | undefined;
  preKey: string | null;
  value: DefinitionRoleInfo | null;
  onChange: (role: DefinitionRoleInfo) => void;
  /**
   * 사용 중 idl_key 중 빈도 최고를 default 로 자동 적용.
   * onChange 와 달리 카드 상태(openCard)를 건드리지 않기 위해 분리.
   */
  onDefaultResolved: (role: DefinitionRoleInfo) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  // PL 이 정해지면 default 자동 적용을 위해 콤보 열림 여부와 무관하게 prefetch.
  const { data, isLoading, isError } = useQuery({
    ...queries.workbook(workbookId!)._ctx.definitionRoles(preKey!),
    enabled: Boolean(workbookId && preKey),
  });

  // 현재 table 의 cells 에서 사용 중인 roleKey 별 빈도. 캐시 재활용.
  const { data: cellsData } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
    enabled: Boolean(workbookId && tableId),
  });

  // 현재 PL preKey 와 매칭되는 cell.roleKey 들만 사용 중으로 본다 (다른 PL 의 roleKey 는 제외).
  const usedRoleKeyCount = useMemo(() => {
    const map = new Map<string, number>();
    if (!cellsData || !preKey) return map;
    const prefix = `${preKey}_`;
    for (const c of cellsData.cells) {
      const rk = c.taggedInfos.roleKey;
      if (rk && rk.startsWith(prefix)) {
        map.set(rk, (map.get(rk) ?? 0) + 1);
      }
    }
    return map;
  }, [cellsData, preKey]);

  // 정렬: (1) 사용 중인 role 이 위 (2) idl_key 숫자 오름차순.
  const roles = useMemo(() => {
    const raw = data?.roles ?? [];
    const idlKeyOf = (key: string) => {
      const last = key.split("_").pop();
      return Number.parseInt(last ?? "0", 10) || 0;
    };
    return [...raw].sort((a, b) => {
      const au = usedRoleKeyCount.has(a.key);
      const bu = usedRoleKeyCount.has(b.key);
      if (au !== bu) return au ? -1 : 1;
      return idlKeyOf(a.key) - idlKeyOf(b.key);
    });
  }, [data, usedRoleKeyCount]);

  // default DL: 사용 중 빈도 최고. 사용자가 이미 골랐으면 덮어쓰지 않음.
  useEffect(() => {
    if (value) return;
    if (roles.length === 0 || usedRoleKeyCount.size === 0) return;
    let bestKey: string | null = null;
    let bestCount = 0;
    for (const [k, count] of usedRoleKeyCount) {
      if (count > bestCount) {
        bestCount = count;
        bestKey = k;
      }
    }
    if (!bestKey) return;
    const def = roles.find((r) => r.key === bestKey);
    if (def) onDefaultResolved(def);
  }, [roles, usedRoleKeyCount, value, onDefaultResolved]);

  return (
    <RoleCombobox
      label="표 유형 선택하기"
      placeholder={preKey ? "표를 선택하세요" : "먼저 주석 목차를 선택해주세요"}
      searchPlaceholder="key 또는 라벨로 검색"
      disabled={!preKey}
      open={open}
      onOpenChange={onOpenChange}
      isLoading={isLoading}
      isError={isError}
      items={roles}
      getItemKey={(r) => r.key}
      getSearchHaystack={(r) => `${r.key} ${r.label}`}
      isSelected={(r) => r.key === value?.key}
      onSelect={onChange}
      renderTrigger={() =>
        value ? (
          <RoleTriggerContent
            badge={value.key.replaceAll("_", " ")}
            title={value.label}
            isUsed={usedRoleKeyCount.has(value.key)}
          />
        ) : null
      }
      renderItem={(r) => (
        <RoleItem
          badge={r.key.replaceAll("_", " ")}
          title={r.label}
          isUsed={usedRoleKeyCount.has(r.key)}
        />
      )}
    />
  );
};

type RoleComboboxProps<T> = {
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  items: T[];
  getItemKey: (item: T) => string;
  getSearchHaystack: (item: T) => string;
  isSelected: (item: T) => boolean;
  onSelect: (item: T) => void;
  renderTrigger: () => ReactNode;
  renderItem: (item: T) => ReactNode;
  /** CommandList 마지막에 렌더링되는 슬롯 (e.g. "더보기" 버튼). */
  listFooter?: ReactNode;
};

/**
 * 인라인 expansion 콤보박스.
 *
 * 펼치면 부모 flex container 의 남은 공간을 차지하도록 `flex-1 min-h-0` 가 켜진다.
 * 닫히면 트리거 높이만 차지 (`flex-none`).
 * Popover/Portal 을 쓰지 않고 같은 flex 흐름 안에 머무르므로 패널 바닥까지 자연스럽게 늘어난다.
 */
const RoleCombobox = <T,>({
  label,
  placeholder,
  searchPlaceholder,
  disabled = false,
  open,
  onOpenChange,
  isLoading,
  isError,
  items,
  getItemKey,
  getSearchHaystack,
  isSelected,
  onSelect,
  renderTrigger,
  renderItem,
  listFooter,
}: RoleComboboxProps<T>) => {
  const [query, setQuery] = useState("");
  // cmdk 가 마우스/키보드 nav 로 자동 부여하는 data-selected 값을 controlled 로 잡는다.
  // 마우스가 CommandList 밖으로 나갔을 때 reset 해야 footer hover 가 마지막 item 까지
  // 강조하는 현상을 막을 수 있다 (CommandItem 의 shadcn 기본 data-[selected=true]:bg-accent).
  const [cmdkValue, setCmdkValue] = useState("");
  // 표준 택소노미 role label 일부가 NFD(자모 분해형)로 저장돼 있어, 단순 includes 로는
  // NFC 검색어와 매칭되지 않는다 (e.g. "금융부채" → D822310_183).
  // filterBySearchText 가 양쪽을 NFC 로 맞춘 뒤 비교한다.
  const filteredItems = useMemo(
    () => filterBySearchText(items, query, getSearchHaystack),
    [items, query, getSearchHaystack],
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setCmdkValue("");
    }
  }, [open]);

  return (
    <div
      className={cn(
        "flex flex-col gap-1 min-h-0",
        open ? "flex-1" : "flex-none",
      )}
    >
      <span className="text-[12px] font-medium text-neutral-500 px-1">
        {label}
      </span>

      {open ? (
        // 펼친 상태: 검색창 박스와 리스트 박스를 시각적으로 분리.
        // gap-2 로 사이를 띄우고, 검색창은 강조 border, 리스트는 가벼운 border.
        <Command
          shouldFilter={false}
          className="flex flex-col gap-2 min-h-0 flex-1"
          value={cmdkValue}
          onValueChange={setCmdkValue}
        >
          <div className="shrink-0 flex items-center border border-blue-200 rounded-md bg-white shadow-sm overflow-hidden justify-between">
            <CommandInput
              autoFocus
              placeholder={searchPlaceholder}
              value={query}
              onValueChange={setQuery}
              className="flex-1 placeholder:text-neutral-400"
            />
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="shrink-0 p-2 mr-1 hover:bg-black/[.03] rounded-md"
              aria-label="닫기"
            >
              <ChevronDown className="w-4 h-4 text-neutral-400 rotate-180 transition-transform" />
            </button>
          </div>

          <div className="flex-1 min-h-0 border border-neutral-200 rounded-md bg-white overflow-hidden flex flex-col">
            <CommandList
              className="flex-1 max-h-none overflow-y-auto"
              onMouseLeave={() => setCmdkValue("")}
            >
              <RoleListState
                isLoading={isLoading}
                isError={isError}
                isEmptyAfterFetch={!isLoading && items.length === 0}
                isEmptyAfterSearch={
                  !isLoading && items.length > 0 && filteredItems.length === 0
                }
              />
              {filteredItems.map((item) => {
                const key = getItemKey(item);
                const selected = isSelected(item);
                return (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => onSelect(item)}
                    className={cn(
                      "group w-full p-3 transition-colors cursor-pointer",
                      "hover:bg-black/[.03] active:bg-black/6",
                      "flex items-center gap-2",
                      selected && "bg-black/6",
                    )}
                  >
                    <Check
                      className={cn(
                        "w-4 h-4 shrink-0 transition-opacity",
                        selected
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-40 group-data-[selected=true]:opacity-40",
                      )}
                    />
                    <div className="flex-1 min-w-0">{renderItem(item)}</div>
                  </CommandItem>
                );
              })}
            </CommandList>
            {/* footer 는 CommandList 밖에 둬야 cmdk 의 selection/hover 상태와 충돌하지 않는다. */}
            {listFooter && <div className="shrink-0">{listFooter}</div>}
          </div>
        </Command>
      ) : (
        <div className="border border-neutral-200 rounded-md bg-white overflow-hidden flex-none">
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            onClick={() => !disabled && onOpenChange(true)}
            className={cn(
              "w-full px-3 py-2 text-left",
              "hover:bg-black/[.03] disabled:opacity-50 disabled:cursor-not-allowed",
              "flex items-center justify-between gap-2",
            )}
          >
            <div className="flex-1 min-w-0">
              {renderTrigger() ?? (
                <span className="text-[14px] text-neutral-400">
                  {placeholder}
                </span>
              )}
            </div>
            <ChevronDown className="w-4 h-4 text-neutral-400 shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
};

const RoleTriggerContent = ({
  badge,
  title,
  isUsed,
  reportType,
}: {
  badge: string;
  title: string;
  isUsed?: boolean;
  reportType?: ReportType | null;
}) => (
  <div className="flex items-center gap-2 min-w-0">
    <span className="shrink-0 inline-flex items-center h-[18px] px-[3px] py-0 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-sm tabular-nums">
      {badge}
    </span>
    {reportType && <TypeChip type={reportType} size="small" />}
    {isUsed && <UsedChip />}
    <span className="text-[14px] font-medium text-neutral-700 truncate">
      {title}
    </span>
  </div>
);

const RoleListState = ({
  isLoading,
  isError,
  isEmptyAfterFetch,
  isEmptyAfterSearch,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmptyAfterFetch: boolean;
  isEmptyAfterSearch: boolean;
}) => {
  if (isLoading) {
    return (
      <p className="text-[13px] text-neutral-500 px-3 py-2">불러오는 중...</p>
    );
  }
  if (isError) {
    return <p className="text-[13px] text-red-500 px-3 py-2">조회 실패</p>;
  }
  if (isEmptyAfterFetch) {
    return (
      <p className="text-[13px] text-neutral-500 px-3 py-2">후보가 없습니다.</p>
    );
  }
  if (isEmptyAfterSearch) {
    return (
      <p className="text-[13px] text-neutral-500 px-3 py-2">
        검색 결과가 없습니다.
      </p>
    );
  }
  return null;
};

const RoleItem = ({
  badge,
  title,
  description,
  isUsed,
  reportType,
}: {
  badge: string;
  title: string;
  description?: string;
  isUsed?: boolean;
  reportType?: ReportType | null;
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <div className="flex items-center gap-1.5">
      <span className="inline-flex items-center h-[18px] px-[3px] py-0 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-sm w-fit tabular-nums">
        {badge}
      </span>
      {reportType && <TypeChip type={reportType} size="small" />}
      {isUsed && <UsedChip />}
    </div>
    <p className="text-[14px] font-medium text-neutral-700 truncate">{title}</p>
  </div>
);

/** 현재 table 에서 사용 중인 role 임을 나타내는 chip. */
const UsedChip = () => (
  <span className="inline-flex items-center h-[18px] px-1.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded-sm shrink-0">
    사용 중
  </span>
);
